import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { BackButton } from "@/components/BackButton";
import { FormField, SelectField } from "@/components/FormField";
import { PrimaryButton } from "@/components/PrimaryButton";
import { ProfitabilityTabs } from "@/components/ProfitabilityTabs";

export const Route = createFileRoute("/tools/roi")({
  component: ROICalculatorPage,
});

type Mode = "roi" | "price";

const PLATFORM_OPTIONS = [
  "eBay",
  "TCGplayer",
  "Facebook Marketplace",
  "Local",
  "Custom",
];
// NOTE (TNK-48/46): platform fees are hardcoded + duplicated across roi /
// breakeven / lot-analyzer / pricetrack. Refactor → read from a single Settings
// "default fees" source, and merge ROI + Break-Even into one "Profit Tools"
// screen sharing one fee card. See docs/settings-audit.md + docs/tools-organization-review.md.
const PLATFORM_FEES: Record<string, number> = {
  eBay: 13.25,
  TCGplayer: 10.25,
  "Facebook Marketplace": 5,
  Local: 0,
  Custom: 0,
};

const HIGHLIGHT_KEYS = new Set([
  "Net Profit",
  "ROI",
  "Required Sell Price",
  "Projected ROI",
]);

function ROICalculatorPage() {
  const [mode, setMode] = useState<Mode>("roi");
  const [platform, setPlatform] = useState("eBay");
  const [customFee, setCustomFee] = useState("13.25");

  const [costBasis, setCostBasis] = useState("");
  const [shippingCost, setShippingCost] = useState("");
  const [otherCosts, setOtherCosts] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [buyerShipping, setBuyerShipping] = useState("");
  const [paymentFee, setPaymentFee] = useState("2.9");

  const [targetROI, setTargetROI] = useState("50");
  const [targetMargin, setTargetMargin] = useState("30");
  const [priceMode, setPriceMode] = useState<"roi" | "margin">("margin");

  const [result, setResult] = useState<Record<string, string> | null>(null);

  const platformFeeRate =
    platform === "Custom"
      ? parseFloat(customFee) || 0
      : (PLATFORM_FEES[platform] ?? 0);

  const calculate = () => {
    const cost = parseFloat(costBasis) || 0;
    const shipping = parseFloat(shippingCost) || 0;
    const other = parseFloat(otherCosts) || 0;
    const totalCost = cost + shipping + other;

    if (mode === "roi") {
      const sell = parseFloat(salePrice) || 0;
      const bShip = parseFloat(buyerShipping) || 0;
      const gross = sell + bShip;
      const platformFees = (gross * platformFeeRate) / 100;
      const payFees = (gross * (parseFloat(paymentFee) || 2.9)) / 100;
      const netProfit =
        gross - platformFees - payFees - shipping - other - cost;
      const roi = totalCost > 0 ? (netProfit / totalCost) * 100 : 0;
      const margin = gross > 0 ? (netProfit / gross) * 100 : 0;
      setResult({
        "Total Cost Basis": `$${totalCost.toFixed(2)}`,
        "Gross Revenue": `$${gross.toFixed(2)}`,
        "Platform Fees": `-$${platformFees.toFixed(2)}`,
        "Payment Fees": `-$${payFees.toFixed(2)}`,
        "Net Profit": `${netProfit >= 0 ? "+" : ""}$${netProfit.toFixed(2)}`,
        ROI: `${roi.toFixed(1)}%`,
        "Profit Margin": `${margin.toFixed(1)}%`,
      });
      return;
    }

    let requiredSell = 0;
    const feeMultiplier =
      1 - (platformFeeRate + (parseFloat(paymentFee) || 2.9)) / 100;
    if (priceMode === "margin") {
      const targetM = (parseFloat(targetMargin) || 0) / 100;
      requiredSell =
        feeMultiplier > 0 && targetM < 1
          ? (totalCost + shipping) / (feeMultiplier * (1 - targetM))
          : 0;
    } else {
      const targetR = (parseFloat(targetROI) || 0) / 100;
      requiredSell =
        feeMultiplier > 0 ? (totalCost * (1 + targetR)) / feeMultiplier : 0;
    }
    const minBreakeven = feeMultiplier > 0 ? totalCost / feeMultiplier : 0;
    const actualProfit = requiredSell * feeMultiplier - totalCost;
    const actualROI = totalCost > 0 ? (actualProfit / totalCost) * 100 : 0;
    const actualMargin =
      requiredSell > 0 ? (actualProfit / requiredSell) * 100 : 0;
    setResult({
      "Required Sell Price": `$${requiredSell.toFixed(2)}`,
      "Break-Even Price": `$${minBreakeven.toFixed(2)}`,
      "Projected Net Profit": `+$${actualProfit.toFixed(2)}`,
      "Projected ROI": `${actualROI.toFixed(1)}%`,
      "Profit Margin": `${actualMargin.toFixed(1)}%`,
      "Platform Fee Rate": `${platformFeeRate.toFixed(2)}%`,
    });
  };

  return (
    <section className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 pb-3">
        <BackButton fallback={{ to: "/tools" }} label="Tools" />
      </div>

      <div className="border-b border-border pb-3">
        <h1 className="text-2xl font-bold text-foreground">Profitability</h1>
        <p className="text-sm text-muted-foreground">
          Per-sale ROI projection — calculate profit and ROI, or find the sell
          price to hit your target margin or return.
        </p>
      </div>

      <ProfitabilityTabs current="roi" />

      <div
        className="flex-1 space-y-4 overflow-y-auto py-4 pr-1"
        style={{ scrollbarGutter: "stable" }}
      >
        {/* Mode toggle */}
        <ModeToggle
          mode={mode}
          onChange={(m) => {
            setMode(m);
            setResult(null);
          }}
          options={[
            { value: "roi", label: "Calculate ROI" },
            { value: "price", label: "Find Sell Price" },
          ]}
        />

        {/* Cost Inputs */}
        <Section title="Cost Inputs">
          <FormField
            label="Cost Basis ($)"
            value={costBasis}
            onChangeText={setCostBasis}
            inputMode="decimal"
            placeholder="0.00"
            required
          />
          <FormField
            label="Shipping / Postage ($)"
            value={shippingCost}
            onChangeText={setShippingCost}
            inputMode="decimal"
            placeholder="0.00"
          />
          <FormField
            label="Other Costs ($)"
            value={otherCosts}
            onChangeText={setOtherCosts}
            inputMode="decimal"
            placeholder="packaging, supplies…"
          />
        </Section>

        {/* Platform */}
        <Section title="Platform">
          <SelectField
            label="Selling Platform"
            value={platform}
            options={PLATFORM_OPTIONS}
            onSelect={(v) => {
              setPlatform(v);
              setResult(null);
            }}
          />
          {platform === "Custom" && (
            <FormField
              label="Platform Fee %"
              value={customFee}
              onChangeText={setCustomFee}
              inputMode="decimal"
              placeholder="13.25"
            />
          )}
          <FormField
            label="Payment Processing Fee %"
            value={paymentFee}
            onChangeText={setPaymentFee}
            inputMode="decimal"
            placeholder="2.9"
          />
          <div className="rounded-md bg-secondary/15 px-3 py-2 text-xs font-medium text-secondary">
            {platform} platform fee: {platformFeeRate.toFixed(2)}%
          </div>
        </Section>

        {/* Sale Inputs (ROI mode) or Target (Price mode) */}
        {mode === "roi" ? (
          <Section title="Sale Inputs">
            <FormField
              label="Sale Price ($)"
              value={salePrice}
              onChangeText={setSalePrice}
              inputMode="decimal"
              placeholder="0.00"
              required
            />
            <FormField
              label="Buyer-Paid Shipping ($)"
              value={buyerShipping}
              onChangeText={setBuyerShipping}
              inputMode="decimal"
              placeholder="0.00"
            />
          </Section>
        ) : (
          <Section title="Target">
            <ModeToggle
              mode={priceMode}
              onChange={setPriceMode}
              options={[
                { value: "margin", label: "Target Margin %" },
                { value: "roi", label: "Target ROI %" },
              ]}
              size="sm"
            />
            {priceMode === "margin" ? (
              <FormField
                label="Target Profit Margin %"
                value={targetMargin}
                onChangeText={setTargetMargin}
                inputMode="decimal"
                placeholder="30"
              />
            ) : (
              <FormField
                label="Target ROI %"
                value={targetROI}
                onChangeText={setTargetROI}
                inputMode="decimal"
                placeholder="50"
              />
            )}
          </Section>
        )}

        <PrimaryButton
          label="Calculate"
          onClick={calculate}
          className="w-full"
        />

        {result && (
          <div className="space-y-1 rounded-md bg-primary p-4">
            <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-primary-foreground/60">
              Results
            </div>
            {Object.entries(result).map(([k, v], i, arr) => (
              <div
                key={k}
                className={`flex items-center justify-between py-2 ${
                  i < arr.length - 1 ? "border-b border-white/10" : ""
                }`}
              >
                <div className="text-sm text-primary-foreground/70">{k}</div>
                <div
                  className={`text-base font-bold ${
                    HIGHLIGHT_KEYS.has(k)
                      ? "text-secondary"
                      : "text-primary-foreground"
                  }`}
                >
                  {v}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1 rounded-md border border-border bg-card p-4">
      <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

interface ModeToggleProps<T extends string> {
  mode: T;
  onChange: (m: T) => void;
  options: { value: T; label: string }[];
  size?: "sm" | "md";
}

function ModeToggle<T extends string>({
  mode,
  onChange,
  options,
  size = "md",
}: ModeToggleProps<T>) {
  const padY = size === "sm" ? "py-1.5" : "py-2.5";
  return (
    <div className="flex gap-1 rounded-md bg-card p-1">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={`flex-1 rounded-md text-center text-xs font-semibold ${padY} ${
            mode === opt.value
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
